/**
 * Google Reviews Widget - Vanilla JavaScript Carousel
 * No jQuery dependency - Pure JavaScript with drag support
 */

(function() {
    'use strict';

    // Google Reviews Carousel Class
    class GoogleReviewsCarousel {
        constructor(element, options) {
            this.element = element;
            this.widget = element.closest('.testimonial-google-style');
            this.options = Object.assign({}, this.defaults, options);
            this.currentIndex = 0;
            this.itemsCount = 0;
            this.itemsPerView = 1;
            this.autoplayInterval = null;

            // Drag properties
            this.isDragging = false;
            this.startX = 0;
            this.currentX = 0;
            this.startTranslate = 0;
            this.currentTranslate = 0;
            this.dragThreshold = 50; // Minimum drag distance to trigger slide change

            this.init();
        }

        get defaults() {
            return {
                items: 1,
                margin: 16,
                loop: true,
                autoplay: false,
                autoplaySpeed: 3000,
                autoplayHoverPause: true,
                nav: true,
                dots: true,
                drag: true,
                responsive: {
                    0: { items: 1 },
                    768: { items: 2 },
                    1024: { items: 3 },
                    1280: { items: 4 }
                }
            };
        }

        init() {
            console.log('Initializing Google Reviews Carousel:', this.options);

            // Build carousel structure
            this.buildCarousel();

            // Build navigation first (before updateResponsive calls updateNav)
            if (this.options.nav) {
                this.buildNav();
            }

            // Build dots
            if (this.options.dots) {
                this.buildDots();
            }

            // Calculate responsive items (this will call updateNav and updateDots)
            this.updateResponsive();

            // Setup events
            this.setupEvents();

            // Start autoplay
            if (this.options.autoplay) {
                this.startAutoplay();
            }

            // Mark as loaded
            this.element.classList.add('gr-loaded');
        }

        buildCarousel() {
            const items = Array.from(this.element.children);
            this.itemsCount = items.length;
            this.originalItemsCount = items.length; // Store original count (without clones)

            // Create stage structure
            const stageOuter = document.createElement('div');
            stageOuter.className = 'gr-stage-outer';
            const stage = document.createElement('div');
            stage.className = 'gr-stage';

            // Move items to stage
            items.forEach(item => {
                item.classList.add('gr-item');
                stage.appendChild(item);
            });

            stageOuter.appendChild(stage);
            this.element.appendChild(stageOuter);

            this.stage = stage;
            this.stageOuter = stageOuter;

            // Add carousel class
            this.element.classList.add('gr-carousel', 'gr-loading');

            // Clone items for infinite loop
            if (this.options.loop && this.itemsCount > 0) {
                this.updateClones();
            }
        }

        updateClones() {
            // Remove existing clones
            this.stage.querySelectorAll('.gr-cloned').forEach(clone => clone.remove());

            // Only clone if we have enough items
            if (this.itemsCount <= this.itemsPerView) return;

            const items = Array.from(this.stage.querySelectorAll('.gr-item:not(.gr-cloned)'));

            // Clone items for seamless loop
            const cloneCount = Math.max(this.itemsPerView, 1);

            // Clone last items and prepend
            for (let i = this.itemsCount - 1; i >= Math.max(0, this.itemsCount - cloneCount); i--) {
                const clone = items[i].cloneNode(true);
                clone.classList.add('gr-cloned');
                this.stage.insertBefore(clone, this.stage.firstChild);
            }

            // Clone first items and append
            for (let i = 0; i < Math.min(cloneCount, this.itemsCount); i++) {
                const clone = items[i].cloneNode(true);
                clone.classList.add('gr-cloned');
                this.stage.appendChild(clone);
            }
        }

        updateResponsive() {
            const width = window.innerWidth;
            let newItemsPerView = this.options.items;

            // Check responsive breakpoints
            const breakpoints = Object.keys(this.options.responsive).sort((a, b) => b - a);
            for (const breakpoint of breakpoints) {
                if (width >= parseInt(breakpoint)) {
                    newItemsPerView = this.options.responsive[breakpoint].items;
                    break;
                }
            }

            if (newItemsPerView !== this.itemsPerView) {
                this.itemsPerView = newItemsPerView;

                // Update clones for new items per view
                if (this.options.loop) {
                    this.updateClones();
                }

                // Rebuild dots for new items per view
                if (this.options.dots && this.dots) {
                    this.rebuildDots();
                }

                this.updateStage();
            }
        }

        updateStage() {
            const gap = this.options.margin;

            // Get container width - use clientWidth for accurate inner width
            // clientWidth excludes scrollbar and gives the actual content area
            const containerWidth = this.stageOuter.clientWidth;

            console.log('Container width:', containerWidth, 'Items per view:', this.itemsPerView);

            // Pure pixel-based calculation like professional carousels
            // Total gap space for n items = gap * (n - 1)
            const totalGapSpace = gap * (this.itemsPerView - 1);

            // Available space for items = container width - total gaps
            const availableSpace = containerWidth - totalGapSpace;

            // Each item width in pixels
            const itemWidthPx = availableSpace / this.itemsPerView;

            console.log('Item width:', itemWidthPx, 'Gap:', gap, 'Total gap space:', totalGapSpace);

            // Set item widths and margins in pixels
            this.stage.querySelectorAll('.gr-item').forEach((item, index) => {
                item.style.width = itemWidthPx + 'px';
                // Only add margin if there are multiple items per view
                item.style.marginRight = this.itemsPerView > 1 ? gap + 'px' : '0px';
            });

            // Update position
            this.goTo(this.currentIndex, false);

            // Update dots
            if (this.options.dots) {
                this.updateDots();
            }
        }

        goTo(index, animate = true) {
            const maxIndex = Math.max(0, this.itemsCount - this.itemsPerView);

            // Store original index for loop handling
            let targetIndex = index;

            // Handle loop boundaries
            if (this.options.loop && this.itemsCount > this.itemsPerView) {
                // Allow going beyond boundaries for smooth loop
                if (index < 0) {
                    targetIndex = maxIndex;
                } else if (index > maxIndex) {
                    targetIndex = 0;
                }
            } else {
                // Clamp to valid range when not looping
                targetIndex = Math.max(0, Math.min(index, maxIndex));
            }

            this.currentIndex = targetIndex;

            // Calculate transform in PIXELS (not percentage)
            const gap = this.options.margin;
            // Use clientWidth for accurate inner width (excludes scrollbar)
            const containerWidth = this.stageOuter.clientWidth;
            const totalGapSpace = gap * (this.itemsPerView - 1);
            const availableSpace = containerWidth - totalGapSpace;
            const itemWidthPx = availableSpace / this.itemsPerView;

            // Each slide movement = item width + gap (only add gap if multiple items per view)
            const slideWidth = this.itemsPerView > 1 ? itemWidthPx + gap : itemWidthPx;

            // Calculate offset for loop (number of cloned items at start)
            const offset = this.options.loop && this.itemsCount > this.itemsPerView ? this.itemsPerView : 0;

            // Calculate translate in pixels
            const translateX = -((targetIndex + offset) * slideWidth);

            // Apply transform
            if (animate) {
                this.stage.classList.add('gr-animating');
                this.stage.classList.remove('gr-no-transition');
            } else {
                this.stage.classList.add('gr-no-transition');
                this.stage.classList.remove('gr-animating');
            }

            this.stage.style.transform = `translateX(${translateX}px)`;
            this.currentTranslate = translateX;

            // Handle infinite loop wrap-around
            if (this.options.loop && this.itemsCount > this.itemsPerView) {
                setTimeout(() => {
                    // If we went past the end, jump to the beginning without animation
                    if (index > maxIndex) {
                        this.stage.classList.add('gr-no-transition');
                        this.stage.classList.remove('gr-animating');
                        const jumpTranslate = -((0 + offset) * slideWidth);
                        this.stage.style.transform = `translateX(${jumpTranslate}px)`;
                        this.currentTranslate = jumpTranslate;
                        this.currentIndex = 0;

                        // Force reflow
                        this.stage.offsetHeight;
                    }
                    // If we went before the start, jump to the end without animation
                    else if (index < 0) {
                        this.stage.classList.add('gr-no-transition');
                        this.stage.classList.remove('gr-animating');
                        const jumpTranslate = -((maxIndex + offset) * slideWidth);
                        this.stage.style.transform = `translateX(${jumpTranslate}px)`;
                        this.currentTranslate = jumpTranslate;
                        this.currentIndex = maxIndex;

                        // Force reflow
                        this.stage.offsetHeight;
                    }

                    this.stage.classList.remove('gr-animating', 'gr-no-transition');
                }, animate ? 450 : 0);
            } else {
                setTimeout(() => {
                    this.stage.classList.remove('gr-animating', 'gr-no-transition');
                }, animate ? 450 : 0);
            }

            // Update navigation state
            this.updateNav();

            // Update dots
            if (this.options.dots) {
                this.updateDots();
            }
        }

        next() {
            this.goTo(this.currentIndex + 1);
        }

        prev() {
            this.goTo(this.currentIndex - 1);
        }

        buildNav() {
            const navHTML = `
                <div class="gr-nav">
                    <button class="gr-prev" type="button">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="15 18 9 12 15 6"></polyline>
                        </svg>
                    </button>
                    <button class="gr-next" type="button">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="9 18 15 12 9 6"></polyline>
                        </svg>
                    </button>
                </div>
            `;

            // Append to carousel element
            this.element.insertAdjacentHTML('beforeend', navHTML);
            this.nav = this.element.querySelector('.gr-nav');

            // Bind events
            this.nav.querySelector('.gr-prev').addEventListener('click', () => this.prev());
            this.nav.querySelector('.gr-next').addEventListener('click', () => this.next());
        }

        updateNav() {
            if (!this.options.nav || !this.nav) return;

            const prevBtn = this.nav.querySelector('.gr-prev');
            const nextBtn = this.nav.querySelector('.gr-next');

            if (!this.options.loop) {
                prevBtn.classList.toggle('gr-disabled', this.currentIndex === 0);
                nextBtn.classList.toggle('gr-disabled', this.currentIndex >= this.itemsCount - this.itemsPerView);
            }
        }

        buildDots() {
            // Use original items count (without clones) for dots calculation
            const itemsCount = this.originalItemsCount || this.itemsCount;
            const dotsCount = Math.max(1, itemsCount - this.itemsPerView + 1);

            console.log('buildDots - Original Items:', this.originalItemsCount, 'Current Items:', this.itemsCount, 'Items Per View:', this.itemsPerView, 'Dots Count:', dotsCount);

            const dotsContainer = document.createElement('div');
            dotsContainer.className = 'gr-dots';

            for (let i = 0; i < dotsCount; i++) {
                const dot = document.createElement('button');
                dot.type = 'button';
                dot.dataset.index = i;
                if (i === 0) dot.classList.add('gr-active');

                dot.addEventListener('click', () => {
                    this.goTo(parseInt(dot.dataset.index));
                });

                dotsContainer.appendChild(dot);
            }

            this.element.appendChild(dotsContainer);
            this.dots = dotsContainer;
        }

        rebuildDots() {
            // Remove existing dots
            if (this.dots) {
                this.dots.remove();
                this.dots = null;
            }

            // Rebuild dots with new itemsPerView
            this.buildDots();
        }

        updateDots() {
            if (!this.options.dots || !this.dots) return;

            this.dots.querySelectorAll('button').forEach(dot => {
                dot.classList.remove('gr-active');
            });

            const activeDot = this.dots.querySelector(`button[data-index="${this.currentIndex}"]`);
            if (activeDot) {
                activeDot.classList.add('gr-active');
            }
        }

        setupEvents() {
            // Window resize
            this.resizeHandler = () => {
                clearTimeout(this.resizeTimeout);
                this.resizeTimeout = setTimeout(() => {
                    this.updateResponsive();
                }, 250);
            };
            window.addEventListener('resize', this.resizeHandler);

            // Hover pause
            if (this.options.autoplayHoverPause) {
                this.element.addEventListener('mouseenter', () => this.stopAutoplay());
                this.element.addEventListener('mouseleave', () => {
                    if (this.options.autoplay) {
                        this.startAutoplay();
                    }
                });
            }

            // Drag events
            if (this.options.drag) {
                this.setupDragEvents();
            }
        }

        setupDragEvents() {
            // Mouse events
            this.stageOuter.addEventListener('mousedown', (e) => this.dragStart(e));
            this.stageOuter.addEventListener('mousemove', (e) => this.dragMove(e));
            this.stageOuter.addEventListener('mouseup', (e) => this.dragEnd(e));
            this.stageOuter.addEventListener('mouseleave', (e) => this.dragEnd(e));

            // Touch events
            this.stageOuter.addEventListener('touchstart', (e) => this.dragStart(e), { passive: true });
            this.stageOuter.addEventListener('touchmove', (e) => this.dragMove(e), { passive: true });
            this.stageOuter.addEventListener('touchend', (e) => this.dragEnd(e));

            // Prevent default drag behavior on images and links
            this.stageOuter.addEventListener('dragstart', (e) => e.preventDefault());

            // Add cursor style
            this.stageOuter.style.cursor = 'grab';
        }

        dragStart(e) {
            // Stop autoplay during drag
            this.stopAutoplay();

            this.isDragging = true;
            this.startX = this.getPositionX(e);
            this.startTranslate = this.currentTranslate;

            this.stageOuter.style.cursor = 'grabbing';
            this.stage.classList.add('gr-dragging');
            this.stage.classList.remove('gr-animating');
        }

        dragMove(e) {
            if (!this.isDragging) return;

            this.currentX = this.getPositionX(e);
            const diff = this.currentX - this.startX;

            // Apply drag transform in pixels
            const newTranslate = this.startTranslate + diff;
            this.stage.style.transform = `translateX(${newTranslate}px)`;
        }

        dragEnd() {
            if (!this.isDragging) return;

            this.isDragging = false;
            this.stageOuter.style.cursor = 'grab';
            this.stage.classList.remove('gr-dragging');

            const diff = this.currentX - this.startX;

            // Determine if we should change slide
            if (Math.abs(diff) > this.dragThreshold) {
                if (diff > 0) {
                    // Dragged right - go to previous
                    this.prev();
                } else {
                    // Dragged left - go to next
                    this.next();
                }
            } else {
                // Snap back to current position
                this.goTo(this.currentIndex, true);
            }

            // Resume autoplay if it was enabled
            if (this.options.autoplay && this.options.autoplayHoverPause) {
                this.startAutoplay();
            }
        }

        getPositionX(e) {
            return e.type.includes('mouse') ? e.pageX : e.touches[0].clientX;
        }

        startAutoplay() {
            this.stopAutoplay();
            this.autoplayInterval = setInterval(() => {
                this.next();
            }, this.options.autoplaySpeed);
        }

        stopAutoplay() {
            if (this.autoplayInterval) {
                clearInterval(this.autoplayInterval);
                this.autoplayInterval = null;
            }
        }

        destroy() {
            this.stopAutoplay();

            if (this.resizeHandler) {
                window.removeEventListener('resize', this.resizeHandler);
            }

            if (this.nav) this.nav.remove();
            if (this.dots) this.dots.remove();
        }
    }

    // Initialize Google Reviews Carousel
    function initGoogleReviewsCarousel() {
        const carousels = document.querySelectorAll('.testimonial-google-style .testimonial-carousel');

        carousels.forEach(carousel => {
            const widget = carousel.closest('.testimonial-google-style');

            // Get settings from data attributes
            const autoplayValue = widget.dataset.autoplay;
            const loopValue = widget.dataset.loop;

            const options = {
                autoplay: autoplayValue === 'true' || autoplayValue === '1' || autoplayValue === true,
                autoplaySpeed: parseInt(widget.dataset.autoplaySpeed) || 3000,
                loop: loopValue === 'true' || loopValue === '1' || loopValue === true || loopValue === undefined
            };

            // Initialize carousel
            new GoogleReviewsCarousel(carousel, options);
        });
    }

    // Read More Modal Functionality
    function initReadMoreModal() {
        // Create modal HTML
        const modalHTML = `
            <div class="gr-modal" id="grReviewModal">
                <div class="gr-modal-overlay"></div>
                <div class="gr-modal-content">
                    <button class="gr-modal-close">&times;</button>
                    <div class="gr-modal-body">
                        <div class="gr-modal-header">
                            <div class="gr-modal-avatar"></div>
                            <div class="gr-modal-author-info">
                                <h3 class="gr-modal-author-name"></h3>
                                <div class="gr-modal-rating"></div>
                                <div class="gr-modal-date"></div>
                            </div>
                        </div>
                        <div class="gr-modal-text"></div>
                    </div>
                </div>
            </div>
        `;

        // Append modal to body if not exists
        if (!document.getElementById('grReviewModal')) {
            document.body.insertAdjacentHTML('beforeend', modalHTML);
        }

        const modal = document.getElementById('grReviewModal');

        // Handle Read More clicks
        document.addEventListener('click', function(e) {
            if (e.target.classList.contains('gr-read-more') || e.target.closest('.gr-read-more')) {
                e.preventDefault();

                const readMoreBtn = e.target.classList.contains('gr-read-more') ? e.target : e.target.closest('.gr-read-more');
                const item = readMoreBtn.closest('.testimonial-item');

                const authorName = item.querySelector('.author-name').textContent;
                const avatar = item.querySelector('.avatar');
                const avatarHtml = avatar.innerHTML;
                const avatarColor = window.getComputedStyle(avatar).backgroundColor;
                const ratingValue = parseInt(item.dataset.rating) || 5;
                const reviewText = item.dataset.fullText;
                const reviewDate = item.dataset.reviewDate || '';

                // Generate SVG stars based on rating
                let starsHtml = '<ul>';
                for (let i = 1; i <= 5; i++) {
                    const starClass = i <= ratingValue ? 'filled' : 'gray';
                    starsHtml += `
                        <li>
                            <svg class="gr-star-icon ${starClass}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16">
                                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                            </svg>
                        </li>
                    `;
                }
                starsHtml += '</ul>';

                // Populate modal
                const modalAvatar = modal.querySelector('.gr-modal-avatar');
                modalAvatar.innerHTML = avatarHtml;
                modalAvatar.style.backgroundColor = avatarColor;

                modal.querySelector('.gr-modal-author-name').textContent = authorName;
                modal.querySelector('.gr-modal-rating').innerHTML = starsHtml;
                modal.querySelector('.gr-modal-date').textContent = reviewDate;
                modal.querySelector('.gr-modal-text').textContent = reviewText;

                // Show modal
                modal.classList.add('active');
                document.body.classList.add('gr-modal-open');
            }
        });

        // Close modal
        document.addEventListener('click', function(e) {
            if (e.target.classList.contains('gr-modal-close') || e.target.classList.contains('gr-modal-overlay')) {
                modal.classList.remove('active');
                document.body.classList.remove('gr-modal-open');
            }
        });

        // Close on ESC key
        document.addEventListener('keydown', function(e) {
            if (e.key === 'Escape' && modal.classList.contains('active')) {
                modal.classList.remove('active');
                document.body.classList.remove('gr-modal-open');
            }
        });
    }

    // Initialize on DOM ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function() {
            initGoogleReviewsCarousel();
            initReadMoreModal();
        });
    } else {
        initGoogleReviewsCarousel();
        initReadMoreModal();
    }

})();
